"use client";

import { useEffect, useRef } from "react";

import { Label } from "@/components/ui";
import { PopoverClose } from "@/components/ui/popover";

import { cx } from "@/lib/tailwind-utils";

export interface EmojiCategory {
  name: string;
  emojis: string[];
}

const DEFAULT_EMOJI_CATEGORIES: EmojiCategory[] = [
  {
    name: "Priority",
    emojis: [
      "🔴",
      "🟠",
      "🟡",
      "🟢",
      "🔵",
      "🟣",
      "⭐",
      "🌟",
      "⚡",
      "🔥",
      "💥",
      "🚨",
      "🏷️",
      "🚩",
      "🔶",
      "🔷",
    ],
  },
  {
    name: "Status",
    emojis: [
      "✅",
      "❌",
      "⚠️",
      "⏳",
      "🔔",
      "📌",
      "🎯",
      "💡",
      "🕐",
      "⏰",
      "🔄",
      "⏸️",
      "▶️",
      "🆕",
      "🔜",
      "✔️",
      "🚫",
      "⛔",
      "❗",
      "❓",
      "🛑",
      "🔁",
      "⏭️",
      "💤",
    ],
  },
  {
    name: "Medical",
    emojis: [
      "💊",
      "🩺",
      "🏥",
      "❤️",
      "🩹",
      "💉",
      "🦷",
      "👁️",
      "🐝",
      "🫁",
      "🦴",
      "🧬",
      "🔬",
      "🩻",
      "🩸",
      "💀",
      "🤧",
      "🤒",
      "🤕",
      "😷",
      "🌡️",
      "⚕️",
      "🧪",
      "🫀",
    ],
  },
  {
    name: "People",
    emojis: [
      "👶",
      "👴",
      "👵",
      "🤰",
      "♿",
      "🏃",
      "💪",
      "🧠",
      "😊",
      "👤",
      "👥",
      "🧒",
      "🧑",
      "👨‍👩‍👧",
      "🐕‍🦺",
      "🦮",
      "🧓",
      "👩",
      "👨",
      "👩‍⚕️",
      "👨‍⚕️",
      "🧑‍⚕️",
      "🗣️",
      "👀",
    ],
  },
  {
    name: "Aesthetics",
    emojis: [
      "💋",
      "👄",
      "💄",
      "💅",
      "💆‍♀️",
      "💆‍♂️",
      "💇‍♀️",
      "💇‍♂️",
      "🧖‍♀️",
      "🧖‍♂️",
      "🧽",
      "🧴",
      "🧼",
      "💧",
      "✨",
      "💎",
      "🪒",
      "🛁",
      "🌹",
      "🦵",
      "🤳",
      "📸",
      "🕶️",
      "👗",
    ],
  },
  {
    name: "Celebrations",
    emojis: [
      "🎂",
      "🎉",
      "🎊",
      "🥳",
      "🎈",
      "🏆",
      "🥇",
      "🏅",
      "💐",
      "🥂",
      "🍾",
      "🙌",
      "👏",
      "🤝",
      "💯",
      "🙏",
    ],
  },
  {
    name: "Feelings",
    emojis: [
      "😀",
      "😍",
      "🥰",
      "😎",
      "🤩",
      "😇",
      "🙂",
      "😐",
      "😕",
      "😟",
      "😢",
      "😠",
      "😤",
      "🤬",
      "😬",
      "🥺",
    ],
  },
  {
    name: "Documents",
    emojis: ["📋", "📝", "📄", "📑", "✍️", "📃", "🗒️", "📎"],
  },
  {
    name: "Admin",
    emojis: [
      "💳",
      "📞",
      "💬",
      "📧",
      "🔒",
      "👑",
      "🎁",
      "💰",
      "📁",
      "🗂️",
      "✏️",
      "🖨️",
      "📅",
      "🤖",
      "💻",
      "🔧",
      "💵",
      "💸",
      "🏦",
      "🧾",
      "📲",
      "📍",
      "🚗",
      "✈️",
    ],
  },
  {
    name: "Symbols",
    emojis: [
      "❤️‍🔥",
      "💙",
      "💚",
      "💜",
      "🧡",
      "💛",
      "🖤",
      "🤍",
      "♻️",
      "☮️",
      "✝️",
      "☪️",
      "🕉️",
      "☯️",
      "🏠",
      "🔑",
    ],
  },
  {
    name: "Nature",
    emojis: [
      "🌸",
      "🌿",
      "🍀",
      "🌻",
      "🌊",
      "☀️",
      "🌙",
      "❄️",
      "🐾",
      "🦋",
      "🐱",
      "🐶",
      "🌈",
      "🍃",
      "🌺",
      "🍎",
    ],
  },
];

/** Messenger reaction type string → color emoji. Used to resolve the
 *  `reaction` field from Meta's API/webhooks into a renderable character. */
export const MESSENGER_REACTION_MAP: Record<string, string> = {
  love: "\u{2764}\u{FE0F}", // ❤️
  smile: "\u{1F606}", // 😆
  wow: "\u{1F62E}", // 😮
  sad: "\u{1F622}", // 😢
  angry: "\u{1F620}", // 😠
  like: "\u{1F44D}", // 👍
  dislike: "\u{1F44E}", // 👎
};

export const QUICK_EMOJIS = [
  "\u{1F44D}",
  "\u{2764}\u{FE0F}",
  "\u{1F602}",
  "\u{1F62E}",
  "\u{1F622}",
  "\u{1F389}",
  "\u{1F525}",
  "\u{1F440}",
];

interface QuickEmojiPickerProps {
  onSelect: (emoji: string) => void;
  onClose: () => void;
  /** Positions the popup to the right (true) or left (false). */
  alignRight?: boolean;
  className?: string;
}

export function QuickEmojiPicker({
  onSelect,
  onClose,
  alignRight,
  className,
}: QuickEmojiPickerProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      ref={ref}
      className={cx(
        "absolute z-20 flex items-center gap-0.5 rounded-lg border border-border bg-background px-1 py-0.5 shadow-md",
        alignRight ? "right-0" : "left-0",
        className,
      )}
      onClick={(e) => e.stopPropagation()}
    >
      {QUICK_EMOJIS.map((emoji) => (
        <button
          key={emoji}
          onClick={() => {
            onSelect(emoji);
            onClose();
          }}
          className="rounded p-0.5 text-body transition-colors hover:bg-hover"
        >
          {emoji}
        </button>
      ))}
    </div>
  );
}

interface EmojiPickerProps {
  selectedEmoji?: string;
  onSelect: (emoji: string) => void;
  categories?: EmojiCategory[];
  columns?: number;
  closeOnSelect?: boolean;
}

export function EmojiPicker({
  selectedEmoji,
  onSelect,
  categories = DEFAULT_EMOJI_CATEGORIES,
  columns = 8,
  closeOnSelect = false,
}: EmojiPickerProps) {
  return (
    <div className="space-y-1">
      {categories.map((category) => (
        <div key={category.name}>
          <Label className="text-body">{category.name}</Label>
          <div
            className="grid gap-1"
            style={{
              gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
            }}
          >
            {category.emojis.map((emoji) => {
              const btn = (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => onSelect(emoji)}
                  className={cx(
                    "flex h-8 w-8 items-center justify-center rounded-md text-lg transition-colors",
                    "hover:bg-hover",
                    selectedEmoji === emoji
                      ? "bg-primary/10 ring-2 ring-primary"
                      : "",
                  )}
                >
                  {emoji}
                </button>
              );

              if (closeOnSelect) {
                return (
                  <PopoverClose asChild key={emoji}>
                    {btn}
                  </PopoverClose>
                );
              }

              return btn;
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
